import { getApiBase } from '@/lib/api-base';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { useEffect, useSyncExternalStore } from 'react';
import {
  cluAnsweredModelOf,
  cluModelPickId,
  cluModelPickOf,
  type CluModel,
  type CluModelPick,
} from './clu-models';

/**
 * The member's own Clu model settings (`/agent/model-defaults`), shared by
 * every picker in the tab: the starred default (Auto or one model), which
 * every new chat and Slack thread starts on, and Auto's preset (Standard,
 * High or Max), the ladder Auto climbs. Read once, then kept here; a change shows
 * at once and goes back when the server refuses it. The server applies the
 * star to new conversations itself. 404 off the router variant of
 * clu_model_v1, where the picker is hidden anyway.
 */

export type CluPreset = 'standard' | 'high' | 'max';

export interface CluPresetLadder {
  id: CluPreset;
  label: string;
  /** The models Auto climbs, in order. */
  models: readonly CluModel[];
}

export interface CluModelDefaults {
  /** The starred row: Auto, or the one model every new chat uses. */
  default: CluModelPick;
  preset: CluPreset;
  presets: readonly CluPresetLadder[];
}

/** Everyone's until they choose: Auto on Standard. */
export const CLU_PRESETS: readonly CluPresetLadder[] = [
  { id: 'standard', label: 'Standard', models: ['luna', 'flash', 'terra'] },
  { id: 'high', label: 'High', models: ['terra', 'glm', 'grok'] },
  { id: 'max', label: 'Max', models: ['sol', 'sonnet55'] },
];
export const CLU_DEFAULTS: CluModelDefaults = {
  default: 'auto',
  preset: 'standard',
  presets: CLU_PRESETS,
};

export type CluModelDefaultsStatus =
  /** Not read yet, or the last read failed. */
  | 'idle'
  | 'loading'
  | 'ready'
  /** 404: the org is off the router. */
  | 'unavailable';

export interface CluModelDefaultsState {
  status: CluModelDefaultsStatus;
  defaults: CluModelDefaults;
  /** Goes up when the settings are dropped for another member's. */
  epoch: number;
}

const PRESET_IDS: readonly CluPreset[] = ['standard', 'high', 'max'];
/** A preset id, its name before 2026-10-28 (low, medium) included. */
const presetOf = (value: unknown): CluPreset | null =>
  value === 'low'
    ? 'standard'
    : value === 'medium'
      ? 'high'
      : PRESET_IDS.includes(value as CluPreset)
        ? (value as CluPreset)
        : null;

function parseLadder(raw: unknown): CluPresetLadder | null {
  if (!raw || typeof raw !== 'object') return null;
  const { id: sentId, label, models } = raw as Record<string, unknown>;
  const id = presetOf(sentId);
  if (!id || !Array.isArray(models)) return null;
  const climb = models
    .map(cluAnsweredModelOf)
    .filter((model): model is CluModel => !!model && model !== 'sonnet5');
  if (climb.length === 0) return null;
  const fallback = CLU_PRESETS.find((preset) => preset.id === id)!;
  // An old id comes with its old name: the portal's is the current one.
  const renamed = id !== sentId;
  return {
    id,
    label:
      !renamed && typeof label === 'string' && label.trim()
        ? label
        : fallback.label,
    models: climb,
  };
}

/**
 * The server's answer, its junk dropped: an unknown default is Auto, an
 * unknown preset Standard, a preset's old id (low, medium) its new one, and a
 * ladder missing or unreadable is the one the portal knows.
 */
export function parseCluModelDefaults(raw: unknown): CluModelDefaults {
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const sent = Array.isArray(body.presets)
    ? body.presets.map(parseLadder).filter((ladder) => ladder !== null)
    : [];
  const presets = CLU_PRESETS.map(
    (preset) => sent.find((ladder) => ladder.id === preset.id) ?? preset
  );
  return {
    default: cluModelPickOf(body.default),
    preset: presetOf(body.preset) ?? 'standard',
    presets,
  };
}

let current: CluModelDefaultsState = {
  status: 'idle',
  defaults: CLU_DEFAULTS,
  epoch: 0,
};
const listeners = new Set<() => void>();
// Whose settings these are (organization and user), once a chat names them.
let owner: string | null = null;
// What the server last reported: a refused change goes back to it.
let confirmed: CluModelDefaults = CLU_DEFAULTS;
// Changes asked for and not sent yet, and the saves' order (see save).
let unsent: { default?: CluModelPick; preset?: CluPreset } = {};
let saveTail: Promise<unknown> = Promise.resolve();
let savesAsked = 0;
// A read or save the server has not answered by then is given up (a refused
// save goes back), and a turn waits on the saves no longer than SAVE_WAIT_MS.
const REQUEST_TIMEOUT_MS = 10_000;
export const SAVE_WAIT_MS = 4_000;

function publish(next: Omit<CluModelDefaultsState, 'epoch'>): void {
  current = { ...next, epoch: current.epoch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const read = () => current;
// Read through a call: an await may have changed it since the last check.
const status = (): CluModelDefaultsStatus => current.status;
const epoch = (): number => current.epoch;

/**
 * `stale` is checked once the token is in hand: the member may have changed
 * while it was fetched, and the token is then the new member's.
 */
async function request(
  stale: () => boolean,
  init?: RequestInit
): Promise<Response> {
  const token = await fetchWebtokenHeadless();
  if (stale()) throw new DOMException('Another member', 'AbortError');
  return fetch(`${getApiBase()}/api/v1/agent/model-defaults`, {
    ...init,
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * The settings belong to `scope` (the organization and user signed in). Kept
 * when the first chat names it; another member's or organization's are
 * dropped, with any read or save still out for them, and read again. The new
 * member's saves do not wait behind the last one's.
 */
export function scopeCluModelDefaults(scope: string): void {
  if (owner === scope) return;
  const first = owner === null;
  owner = scope;
  if (first) return;
  confirmed = CLU_DEFAULTS;
  unsent = {};
  saveTail = Promise.resolve();
  current = { status: 'idle', defaults: CLU_DEFAULTS, epoch: epoch() + 1 };
  for (const listener of listeners) listener();
}

/** Reads the member's settings once; a failed read is tried again later. */
export async function loadCluModelDefaults(): Promise<void> {
  if (current.status !== 'idle') return;
  const mine = epoch();
  publish({ ...current, status: 'loading' });
  // A change made while the read is out wins over what the read brings.
  const before = current.defaults;
  const stale = () => epoch() !== mine || status() !== 'loading';
  try {
    const res = await request(stale);
    if (stale()) return;
    if (res.status === 404) {
      publish({ status: 'unavailable', defaults: CLU_DEFAULTS });
      return;
    }
    if (!res.ok) throw new Error(`Model defaults: ${res.status}`);
    const body: unknown = await res.json();
    if (stale()) return;
    confirmed = parseCluModelDefaults(body);
    publish({
      status: 'ready',
      defaults: current.defaults === before ? confirmed : current.defaults,
    });
  } catch {
    if (!stale()) publish({ ...current, status: 'idle' });
  }
}

/**
 * Stars a row or chooses Auto's preset: shown at once and kept by the server.
 * Saves go out one at a time, in the order asked for; one still waiting when
 * a newer is asked for goes with it. When the server refuses one, the picker
 * goes back to what the server last reported (unless changed again since).
 * True when the server kept it.
 */
export function saveCluModelDefaults(change: {
  default?: CluModelPick;
  preset?: CluPreset;
}): Promise<boolean> {
  const mine = epoch();
  const asked = ++savesAsked;
  unsent = { ...unsent, ...change };
  publish({ ...current, defaults: { ...current.defaults, ...change } });
  const saving = saveTail.then(async (): Promise<boolean> => {
    if (epoch() !== mine) return false;
    if (savesAsked !== asked) return true;
    const sending = unsent;
    unsent = {};
    const body: Record<string, string> = {};
    if (sending.default !== undefined)
      body.default = cluModelPickId(sending.default);
    if (sending.preset !== undefined) body.preset = sending.preset;
    try {
      const res = await request(() => epoch() !== mine, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (epoch() !== mine) return false;
      if (!res.ok) throw new Error(`Model defaults: ${res.status}`);
      const saved: unknown = await res.json().catch(() => null);
      if (epoch() !== mine) return false;
      confirmed = saved
        ? parseCluModelDefaults(saved)
        : { ...confirmed, ...sending };
      if (savesAsked === asked) publish({ ...current, defaults: confirmed });
      return true;
    } catch {
      if (epoch() === mine && savesAsked === asked)
        publish({ ...current, defaults: confirmed });
      return false;
    }
  });
  saveTail = saving.then(
    () => undefined,
    () => undefined
  );
  return saving;
}

/**
 * Settles once every save asked for so far is answered, kept or refused: the
 * server routes a turn on the settings it holds, so a turn sent right after a
 * preset or star change waits for it. Not past SAVE_WAIT_MS, or once `signal`
 * (the turn's own) is aborted: a stalled save must not hold the chat.
 */
export function cluModelDefaultsSaved(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, SAVE_WAIT_MS);
    if (signal?.aborted) return done();
    signal?.addEventListener('abort', done);
    void saveTail.then(done);
  });
}

/** The member's settings, read on first use where `enabled`. */
export function useCluModelDefaults(enabled = true): CluModelDefaultsState {
  const state = useSyncExternalStore(subscribe, read, read);
  useEffect(() => {
    if (enabled) void loadCluModelDefaults();
  }, [enabled, state.epoch]);
  return state;
}

/** What a new chat starts on: the starred row (Auto until one is read). */
export function cluNewChatPick(): CluModelPick {
  return current.defaults.default;
}

/**
 * The same, re-rendering only when it changes (not on a preset or status
 * change: the snapshot is the string). Reads nothing by itself.
 */
export function useCluNewChatPick(): CluModelPick {
  return useSyncExternalStore(subscribe, cluNewChatPick, cluNewChatPick);
}

/** For tests: back to never read, by no one. */
export function resetCluModelDefaults(): void {
  owner = null;
  confirmed = CLU_DEFAULTS;
  unsent = {};
  saveTail = Promise.resolve();
  current = { status: 'idle', defaults: CLU_DEFAULTS, epoch: epoch() + 1 };
  for (const listener of listeners) listener();
}
